import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import {
  FHIR_RESOURCE_TABS,
  FHIR_SAMPLE_INTRO,
} from "../data/fhirSampleBundle";
import {
  applySearchToJsonLines,
  buildJsonPreviewLines,
  filterMappedFields,
  formatBundleJson,
  formatResourceJson,
  getMappedFieldGuide,
  getResourceForTab,
  suggestResourceTabFromSegment,
} from "../data/fhirPreviewHelpers";

const JsonLineTokens = ({ tokens }) => (
  <>
    {tokens.map((token, idx) => {
      if (token.type === "ws") {
        return (
          <span key={idx} className="fhir-token-ws">
            {token.text}
          </span>
        );
      }
      return (
        <span key={idx} className={`fhir-token-${token.type}`}>
          {token.text}
        </span>
      );
    })}
  </>
);

const FhirPreviewDrawer = ({ open, onClose, selectedSegmentId }) => {
  const [activeTab, setActiveTab] = useState("bundle");
  const [searchQuery, setSearchQuery] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState("");

  const { fhirRows, nonFhirRows } = useMemo(() => getMappedFieldGuide(), []);

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredFhirRows = useMemo(
    () => filterMappedFields(fhirRows, normalizedSearch),
    [fhirRows, normalizedSearch]
  );

  const filteredNonFhirRows = useMemo(
    () => filterMappedFields(nonFhirRows, normalizedSearch),
    [nonFhirRows, normalizedSearch]
  );

  const activeResource = useMemo(
    () => getResourceForTab(activeTab),
    [activeTab]
  );

  const jsonLines = useMemo(() => {
    const lines = buildJsonPreviewLines(activeResource);
    return applySearchToJsonLines(lines, normalizedSearch);
  }, [activeResource, normalizedSearch]);

  const exportText = useMemo(() => {
    if (activeTab === "bundle") return formatBundleJson();
    return formatResourceJson(activeResource);
  }, [activeTab, activeResource]);

  useEffect(() => {
    if (!open || !selectedSegmentId) return;
    setActiveTab(suggestResourceTabFromSegment(selectedSegmentId));
  }, [open, selectedSegmentId]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(exportText);
      setCopyStatus("Copied");
    } catch {
      setCopyStatus("Copy failed");
    }
    setTimeout(() => setCopyStatus(""), 2000);
  }, [exportText]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([exportText], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download =
      activeTab === "bundle"
        ? "prior-auth-fhir-sample-bundle.json"
        : `prior-auth-fhir-sample-${activeTab.toLowerCase()}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [exportText, activeTab]);

  return (
    <Offcanvas
      show={open}
      onHide={onClose}
      placement="end"
      className="edi-preview-offcanvas fhir-preview-offcanvas"
      aria-labelledby="fhir-preview-drawer-title"
    >
      <Offcanvas.Header closeButton className="edi-preview-drawer-header">
        <div className="edi-preview-header-content">
          <div className="edi-preview-header-top">
            <span className="edi-preview-header-icon" aria-hidden="true">
              <i className="fh_docs_fill" />
            </span>
            <div className="edi-preview-header-copy">
              <div className="edi-preview-title-row">
                <Offcanvas.Title id="fhir-preview-drawer-title">
                  FHIR Preview
                </Offcanvas.Title>
                <span className="edi-preview-header-badge">Sample Bundle</span>
              </div>
              <p className="edi-preview-drawer-subtitle">
                See where to pass Prior Authorization data before X12 278 generation
              </p>
            </div>
          </div>
          <div className="edi-preview-header-note">
            <i className="fh_info_fill" aria-hidden="true" />
            <p>{FHIR_SAMPLE_INTRO}</p>
          </div>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="edi-preview-offcanvas-body">
        <div className="edi-preview-toolbar">
          <button type="button" className="btn btn-border-primary btn-sm" onClick={handleCopy}>
            Copy
          </button>
          <button type="button" className="btn btn-border-primary btn-sm" onClick={handleDownload}>
            Download .json
          </button>
          <button
            type="button"
            className="btn btn-border-primary btn-sm"
            onClick={() => setGuideOpen((prev) => !prev)}
          >
            {guideOpen ? "Hide Guide" : "Show Guide"}
          </button>
          <div className="edi-preview-search-wrap">
            <input
              type="search"
              className="edi-preview-search"
              placeholder="Search path or field…"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              aria-label="Search FHIR paths"
            />
          </div>
          {copyStatus && (
            <span className="edi-preview-copy-status" role="status">
              {copyStatus}
            </span>
          )}
        </div>

        <div className="fhir-preview-drawer-body">
          {guideOpen && (
            <section className="fhir-preview-guide">
              <h3 className="fhir-preview-section-title">Where to pass data</h3>
              <p className="fhir-preview-guide-desc">
                Fields below are read from the sample FHIR Bundle. Values match the Generated
                EDI preview.
              </p>
              <div className="fhir-table-wrap">
                <table className="fhir-preview-mapped-table">
                  <thead>
                    <tr>
                      <th>Model field</th>
                      <th>FHIR path</th>
                      <th>Example value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFhirRows.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="fhir-preview-empty-row">
                          No mapped fields match your search.
                        </td>
                      </tr>
                    ) : (
                      filteredFhirRows.map((row) => (
                        <tr key={row.binding}>
                          <td>
                            <span className="fhir-field-binding">{row.binding}</span>
                          </td>
                          <td>
                            <span className="fhir-path-cell">
                              {row.mappingSource ?? row.fhirPath}
                            </span>
                            {row.fallbackPath && (
                              <div className="fhir-fallback-path">
                                Fallback: {row.fallbackPath}
                              </div>
                            )}
                            {row.transform && (
                              <div className="fhir-field-note">
                                Transform: {row.transform}
                              </div>
                            )}
                            {row.notes && (
                              <div className="fhir-field-note">{row.notes}</div>
                            )}
                          </td>
                          <td>
                            <span className="fhir-sample-cell">{row.sampleValue}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {filteredNonFhirRows.length > 0 && (
                <>
                  <h3 className="fhir-preview-section-title fhir-preview-section-title-muted">
                    Not from FHIR
                  </h3>
                  <p className="fhir-preview-guide-desc">
                    Envelope, control numbers, and client settings — configured outside the
                    Bundle.
                  </p>
                  <div className="fhir-table-wrap">
                    <table className="fhir-preview-mapped-table fhir-preview-mapped-table-muted">
                      <thead>
                        <tr>
                          <th>Model field</th>
                          <th>Source</th>
                          <th>Example value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredNonFhirRows.map((row) => (
                          <tr key={row.binding}>
                            <td>
                              <span className="fhir-field-binding">{row.binding}</span>
                            </td>
                            <td>
                              <span className="fhir-source-badge">{row.source ?? row.transform}</span>
                              {row.mappingSource && (
                                <div className="fhir-field-note">{row.mappingSource}</div>
                              )}
                              {row.notes && (
                                <div className="fhir-field-note">{row.notes}</div>
                              )}
                            </td>
                            <td>
                              <span className="fhir-sample-cell">{row.sampleValue}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </section>
          )}

          <section className="fhir-preview-json-section">
            <div
              className="fhir-resource-tabs"
              role="tablist"
              aria-label="FHIR resource"
            >
              {FHIR_RESOURCE_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`fhir-resource-tab ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="fhir-preview-lines">
              {jsonLines.map((line) => (
                <div
                  key={`${activeTab}-${line.lineNumber}`}
                  className={`fhir-preview-line ${line.matchesSearch ? "search-hit" : ""}`}
                >
                  <span className="edi-preview-line-number">{line.lineNumber}</span>
                  <code className="edi-preview-line-code">
                    <JsonLineTokens tokens={line.tokens} />
                  </code>
                </div>
              ))}
            </div>
          </section>
        </div>

        <footer className="edi-preview-drawer-footer">
          <span className="edi-legend-item">
            <span className="edi-legend-swatch dynamic" /> Mapped from FHIR
          </span>
          <span className="edi-legend-item">
            <span className="edi-legend-swatch system" /> Client config / system
          </span>
        </footer>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default FhirPreviewDrawer;
